import React, { useState } from 'react';
import { useCloseOnEscape } from '../../hooks/useCloseOnEscape';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './AuthModal.css';

const schema = yup.object({
  email: yup.string().email('Please enter a valid email').required('Email is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required')
});

const VenueLoginModal = ({ isOpen, onClose, onSwitchToSignup }) => {
  const { login, error, clearError } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(schema)
  });

  const handleClose = () => {
    clearError();
    reset();
    onClose();
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await login(data.email, data.password, 'venue_owner');
      handleClose();
      navigate('/venue-dashboard');
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    // TODO: Implement forgot password functionality
  };

  useCloseOnEscape(isOpen, handleClose);

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay" onClick={handleClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={handleClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        <div className="auth-modal-content">
          <div className="auth-modal-header">
            <h2 className="auth-modal-title">Venue Owner Login</h2>
            <p className="auth-modal-subtitle">
              Access your venue dashboard to manage bookings, staff, and performance analytics
            </p>
          </div>

          <div className="auth-modal-body">
            {/* Divider */}
            <div className="auth-divider">
              <span className="auth-divider-text">Business Login</span>
            </div>

            {/* Email/Password Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
              <div className="auth-form-group">
                <input
                  type="email"
                  placeholder="Business Email"
                  className={`auth-input ${errors.email ? 'auth-input-error' : ''}`}
                  {...register('email')}
                />
                {errors.email && (
                  <span className="auth-error-message">{errors.email.message}</span>
                )}
              </div>

              <div className="auth-form-group">
                <input
                  type="password"
                  placeholder="Password"
                  className={`auth-input ${errors.password ? 'auth-input-error' : ''}`}
                  {...register('password')}
                />
                {errors.password && (
                  <span className="auth-error-message">{errors.password.message}</span>
                )}
              </div>

              {error && (
                <div className="auth-error-message auth-error-general">
                  {error}
                </div>
              )}

              <button 
                type="submit" 
                className="auth-submit-btn"
                disabled={isLoading}
              >
                {isLoading ? 'Logging in...' : 'Login to Dashboard'}
              </button>
            </form>

            {/* Forgot Password */}
            <div className="auth-forgot-password">
              <button 
                type="button" 
                className="auth-forgot-link"
                onClick={handleForgotPassword}
              >
                Forgot Password
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="auth-modal-footer">
            <p className="auth-switch-text">
              Don't have a venue account?{' '}
              <button 
                type="button" 
                className="auth-switch-link"
                onClick={onSwitchToSignup}
              >
                Register your venue
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VenueLoginModal;
